import React, { useState } from "react";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PasswordRecoveryScreen = ({ onBack, initialToken = null }) => {
 // Если передан initialToken (пользователь перешёл по ссылке из письма) —
 // сразу показываем шаг 2 с предзаполненным токеном
 const [step, setStep] = useState(initialToken ? 2 : 1);
 const [contact, setContact] = useState("");
 const [token, setToken] = useState(initialToken || "");
 const [newPassword, setNewPassword] = useState("");
 const [confirmPassword, setConfirmPassword] = useState("");
 const [loading, setLoading] = useState(false);
 const [message, setMessage] = useState("");
 const [error, setError] = useState("");
 const [showNewPassword, setShowNewPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);

 const handleRequestSubmit = async (e) => {
 e.preventDefault();
 setLoading(true);
 setError("");
 setMessage("");

 try {
 const response = await axios.post(`${API}/auth/password-recovery/request`, {
 contact,
 method: "email"
 });
 setMessage(response.data.message);
 setStep(2);
 } catch (error) {
 setError(error.response?.data?.detail || "Произошла ошибка");
 } finally {
 setLoading(false);
 }
 };

 const handleVerifySubmit = async (e) => {
 e.preventDefault();
 if (newPassword !== confirmPassword) {
 setError("Пароли не совпадают");
 return;
 }

 setLoading(true);
 setError("");
 setMessage("");

 try {
 const verifyData = {
 token,
 new_password: newPassword
 };

 const response = await axios.post(`${API}/auth/password-recovery/verify`, verifyData);
 setMessage(response.data.message);
 setTimeout(() => {
 onBack();
 }, 2000);
 } catch (error) {
 setError(error.response?.data?.detail || "Произошла ошибка");
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
 <div className="max-w-md w-full space-y-8">
 <div className="text-center">
 <h1 className="text-4xl font-bold text-gray-900 mb-2">Reversia</h1>
 <p className="text-lg text-gray-600">Восстановление пароля</p>
 </div>

 <div className="bg-white p-8 rounded-lg shadow-md">
 <h2 className="text-2xl font-semibold text-center mb-6">
 {step === 1 ? "Забыли пароль?" : "Новый пароль"}
 </h2>

 {error && (
 <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
 {error}
 </div>
 )}

 {message && (
 <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
 {message}
 </div>
 )}

 {step === 1 ? (
 <form onSubmit={handleRequestSubmit} className="space-y-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Email
 </label>
 <input
 type="email"
 required
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 value={contact}
 onChange={(e) =>setContact(e.target.value)}
 placeholder="Введите ваш email"
 data-testid="recovery-contact-input"
 />
 </div>

 <button
 type="submit"
 disabled={loading}
 className="reversia-btn reversia-btn-blue reversia-btn-md w-full"
 >
 {loading ? "Отправка..." : "Отправить"}
 </button>
 </form>
 ) : (
 <form onSubmit={handleVerifySubmit} className="space-y-4">
 {/* Ссылка из письма — токен уже есть, показываем информационное сообщение */}
 {initialToken && (
 <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded text-sm">Ссылка из письма распознана. Введите новый пароль.
 </div>
 )}

 {/* Ручной ввод токена — только если пользователь НЕ пришёл по ссылке из письма */}
 {!initialToken && (
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Токен из email
 </label>
 <input
 type="text"
 required
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 value={token}
 onChange={(e) =>setToken(e.target.value)}
 placeholder="Введите токен из письма"
 />
 </div>
 )}

 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Новый пароль
 </label>
 <div className="relative">
 <input
 type={showNewPassword ? "text" : "password"}
 required
 className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 value={newPassword}
 onChange={(e) =>setNewPassword(e.target.value)}
 placeholder="Введите новый пароль"
 />
 <button
 type="button"
 className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
 onClick={() =>setShowNewPassword(!showNewPassword)}
 >
 {showNewPassword ? (
 <EyeOff className="w-5 h-5" />
 ) : (
 <Eye className="w-5 h-5" />
 )}
 </button>
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Подтвердите пароль
 </label>
 <div className="relative">
 <input
 type={showConfirmPassword ? "text" : "password"}
 required
 className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 value={confirmPassword}
 onChange={(e) =>setConfirmPassword(e.target.value)}
 placeholder="Повторите новый пароль"
 />
 <button
 type="button"
 className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
 onClick={() =>setShowConfirmPassword(!showConfirmPassword)}
 >
 {showConfirmPassword ? (
 <EyeOff className="w-5 h-5" />
 ) : (
 <Eye className="w-5 h-5" />
 )}
 </button>
 </div>
 </div>

 <button
 type="submit"
 disabled={loading}
 className="reversia-btn reversia-btn-green reversia-btn-md w-full"
 >
 {loading ? "Сохранение..." : "Изменить пароль"}
 </button>
 </form>
 )}

 <div className="mt-4 text-center">
 <button
 onClick={onBack}
 className="text-blue-600 hover:text-blue-800"
 >
 ← Вернуться к входу
 </button>
 {step === 2 && (
 <div className="mt-2">
 <button
 onClick={() =>setStep(1)}
 className="text-sm text-gray-600 hover:text-gray-800"
 >Отправить код заново
 </button>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 );
};
